/**
 * Theme Toggle - per `Design/Mockups/_Notes - Theme Toggle.md`.
 *
 * Three-state segmented pill: Light / Dark / System. Container has `muted`
 * background, 8px corner radius, 1px `border` outline. Composes three
 * `theme-segment` instances (sun / moon / monitor).
 *
 * Note: the older `Design/Pencil Prompts/Theme Toggle - Pencil Prompt.md`
 * brief specs two-state. The shipped Pencil mockup and Design Brief lock in
 * three-state. This component implements the three-state contract.
 */

import { mountThemeSegment, type SegmentHandle } from './theme-segment';

export type ThemeValue = 'light' | 'dark' | 'system';

export interface ThemeToggleProps {
  value: ThemeValue;
  onChange: (value: ThemeValue) => void;
}

export interface ToggleHandle {
  update: (props: ThemeToggleProps) => void;
  destroy: () => void;
}

interface SegmentRef {
  value: ThemeValue;
  handle: SegmentHandle;
}

export function mountThemeToggle(
  target: HTMLElement,
  props: ThemeToggleProps,
): ToggleHandle {
  const root = document.createElement('div');
  root.className = 'gv-theme-toggle';
  root.setAttribute('role', 'group');
  root.setAttribute('aria-label', 'Theme');

  let current = props;

  const order: { value: ThemeValue; icon: 'sun' | 'moon' | 'monitor'; label: string }[] = [
    { value: 'light', icon: 'sun', label: 'Light theme' },
    { value: 'dark', icon: 'moon', label: 'Dark theme' },
    { value: 'system', icon: 'monitor', label: 'Match system theme' },
  ];

  const segments: SegmentRef[] = order.map((entry) => {
    const handle = mountThemeSegment(root, {
      icon: entry.icon,
      selected: current.value === entry.value,
      ariaLabel: entry.label,
      onSelect: (): void => {
        if (current.value !== entry.value) {
          current.onChange(entry.value);
        }
      },
    });
    return { value: entry.value, handle };
  });

  function render(p: ThemeToggleProps): void {
    current = p;
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      const entry = order[i];
      if (segment === undefined || entry === undefined) continue;
      segment.handle.update({
        icon: entry.icon,
        selected: p.value === segment.value,
        ariaLabel: entry.label,
        onSelect: (): void => {
          if (current.value !== segment.value) {
            current.onChange(segment.value);
          }
        },
      });
    }
  }

  // `mountThemeSegment` already rendered the initial state.
  target.appendChild(root);

  return {
    update: (next: ThemeToggleProps): void => {
      render(next);
    },
    destroy: (): void => {
      for (const s of segments) s.handle.destroy();
      root.remove();
    },
  };
}
